/** Engagement header: mentee contact, dates, and inline-editable hours (D19). */
import React, { useEffect, useRef, useState } from 'react';
import { Text, View } from 'react-native';

import { parseMentorHours } from '@/utils/mentorFormWorkflow';
import { useTranslation } from '@/hooks/useTranslation';
import type { EngagementDetail, EngagementUpdateRequest } from '@/types/mentorEngagements';
import { ActionButton, Chip, Panel, TextField, mentorStyles } from './shared';
import { isIsoDate } from './SessionMetaModal';

interface EngagementHeaderPanelProps {
  engagement: EngagementDetail;
  readOnly?: boolean;
  onSave: (payload: EngagementUpdateRequest) => Promise<void>;
}

function toText(value: number | string | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

export function EngagementHeaderPanel({ engagement, readOnly = false, onSave }: EngagementHeaderPanelProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState({
    mentee_name: engagement.mentee_name ?? '',
    mentee_email: toText(engagement.mentee_email),
    individual_hours: toText(engagement.individual_hours),
    group_hours: toText(engagement.group_hours),
    started_at: toText(engagement.started_at),
    ended_at: toText(engagement.ended_at),
    previous_mentor_name: toText(engagement.previous_mentor_name),
    mentor_printed_name: toText(engagement.mentor_printed_name),
    notes: toText(engagement.notes),
  });
  const dirty = useRef(false);
  const engagementId = useRef(engagement.id);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (engagementId.current === engagement.id && dirty.current) return;
    engagementId.current = engagement.id;
    dirty.current = false;
    setDraft({
      mentee_name: engagement.mentee_name ?? '',
      mentee_email: toText(engagement.mentee_email),
      individual_hours: toText(engagement.individual_hours),
      group_hours: toText(engagement.group_hours),
      started_at: toText(engagement.started_at),
      ended_at: toText(engagement.ended_at),
      previous_mentor_name: toText(engagement.previous_mentor_name),
      mentor_printed_name: toText(engagement.mentor_printed_name),
      notes: toText(engagement.notes),
    });
  }, [engagement]);

  const individualHours = parseMentorHours(draft.individual_hours);
  const groupHours = parseMentorHours(draft.group_hours);
  const totalHours = individualHours !== null && groupHours !== null ? individualHours + groupHours : null;

  const save = async () => {
    if (saving) return;
    const menteeName = draft.mentee_name.trim() || (engagement.mentee_name ?? '');
    if (!menteeName) {
      setMessage(t('mentorEngagements.validation.menteeNameRequired'));
      return;
    }
    const email = draft.mentee_email.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setMessage(t('mentorEngagements.validation.invalidEmail'));
      return;
    }
    if ([draft.started_at, draft.ended_at].some(value => value.trim() && !isIsoDate(value.trim()))) {
      setMessage(t('mentorEngagements.validation.invalidDate'));
      return;
    }
    if (individualHours === null || groupHours === null) {
      setMessage(t('mentorEngagements.detail.invalidHours'));
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      await onSave({
        mentee_name: menteeName,
        mentee_email: email || null,
        individual_hours: individualHours,
        group_hours: groupHours,
        started_at: draft.started_at.trim() || null,
        ended_at: draft.ended_at.trim() || null,
        previous_mentor_name: draft.previous_mentor_name.trim() || null,
        mentor_printed_name: draft.mentor_printed_name.trim() || null,
        notes: draft.notes.trim() || null,
      });
      dirty.current = false;
      setMessage(t('mentorEngagements.detail.saved'));
    } catch {
      setMessage(t('mentorEngagements.detail.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const field = (key: keyof typeof draft) => (value: string) => {
    dirty.current = true;
    setMessage(null);
    setDraft((current) => ({ ...current, [key]: value }));
  };

  return (
    <Panel testID="engagement_header_panel">
      <View style={[mentorStyles.row, { justifyContent: 'space-between' }]}>
        <Text style={mentorStyles.panelTitle}>{t('mentorEngagements.detail.contact')}</Text>
        <View style={mentorStyles.row}>
          <Chip label={engagement.target_level.toUpperCase()} tone="accent" />
          <Chip label={t(`mentorEngagements.status.${engagement.status}`)} />
        </View>
      </View>

      <View style={mentorStyles.row}>
        <TextField
          testID="engagement_mentee_name"
          label={t('mentorEngagements.create.menteeName')}
          value={draft.mentee_name}
          onChangeText={field('mentee_name')}
          editable={!readOnly && !saving}
        />
        <TextField
          testID="engagement_mentee_email"
          label={t('mentorEngagements.create.menteeEmail')}
          value={draft.mentee_email}
          onChangeText={field('mentee_email')}
          keyboardType="email-address"
          editable={!readOnly && !saving}
        />
      </View>

      <Text style={[mentorStyles.panelTitle, { fontSize: 15, marginTop: 4 }]}>
        {t('mentorEngagements.detail.hours')}
      </Text>
      <View style={mentorStyles.row}>
        <TextField
          testID="engagement_individual_hours"
          label={t('mentorEngagements.detail.individualHours')}
          value={draft.individual_hours}
          onChangeText={field('individual_hours')}
          keyboardType="numeric"
          editable={!readOnly && !saving}
        />
        <TextField
          label={t('mentorEngagements.detail.groupHours')}
          value={draft.group_hours}
          onChangeText={field('group_hours')}
          keyboardType="numeric"
          editable={!readOnly && !saving}
        />
        <View style={{ gap: 6, flexGrow: 1, flexBasis: 140, justifyContent: 'flex-end' }}>
          <Text style={mentorStyles.fieldLabel}>{t('mentorEngagements.detail.totalHours')}</Text>
          <Text style={[mentorStyles.panelTitle, { fontSize: 20 }]}>{totalHours === null ? '—' : totalHours.toFixed(1)}</Text>
        </View>
      </View>

      <View style={mentorStyles.row}>
        <TextField
          label={t('mentorEngagements.detail.startedAt')}
          value={draft.started_at}
          onChangeText={field('started_at')}
          placeholder="YYYY-MM-DD"
          editable={!readOnly && !saving}
        />
        <TextField
          label={t('mentorEngagements.detail.endedAt')}
          value={draft.ended_at}
          onChangeText={field('ended_at')}
          placeholder="YYYY-MM-DD"
          editable={!readOnly && !saving}
        />
        <TextField
          label={t('mentorEngagements.detail.previousMentor')}
          value={draft.previous_mentor_name}
          onChangeText={field('previous_mentor_name')}
          editable={!readOnly && !saving}
        />
      </View>

      <View style={mentorStyles.row}>
        <TextField
          label={t('mentorEngagements.detail.printedName')}
          value={draft.mentor_printed_name}
          onChangeText={field('mentor_printed_name')}
          editable={!readOnly && !saving}
        />
      </View>

      <TextField
        label={t('mentorEngagements.detail.notes')}
        value={draft.notes}
        onChangeText={field('notes')}
        multiline
        editable={!readOnly && !saving}
      />

      {!readOnly ? (
        <View style={mentorStyles.row}>
          <ActionButton
            testID="engagement_header_save"
            label={t('mentorEngagements.detail.save')}
            onPress={save}
            variant="primary"
            icon="save"
            disabled={saving}
          />
          {message ? <Text testID="engagement_header_message" accessibilityLiveRegion="polite" style={mentorStyles.mutedText}>{message}</Text> : null}
        </View>
      ) : null}
    </Panel>
  );
}

export default EngagementHeaderPanel;
